import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy } from '@ngneat/until-destroy';
import { connectState, getTrackByFn } from '@y42/angular/util';
import { ConfirmDialogComponent } from '@y42/core/dialog/confirm-dialog';
import { ToastService } from '@y42/toast';
import { ColumnApi, GridApi } from 'ag-grid-community';
import cloneDeep from 'lodash/cloneDeep';
import { BehaviorSubject, filter, switchMap, tap } from 'rxjs';
import { AbstractListViewService } from '../abstract-list-view.service';
import {
  ListViewAddEditQuickFilterDialogComponent,
  ListViewAddEditQuickFilterDialogData,
} from '../list-view-add-edit-quick-filter-dialog/list-view-add-edit-quick-filter-dialog.component';
import { ListViewDataBase } from '../list-view.model';
import { ListViewQuickFilterKeysService } from '../model/list-view-quick-filter-keys.service';
import { QuickFilterModel } from '../model/quick-filter.model';

@UntilDestroy()
@Component({
  selector: 'y42-list-view-quick-filters',
  templateUrl: './list-view-quick-filters.component.html',
  styleUrls: ['./list-view-quick-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListViewQuickFiltersComponent<D extends ListViewDataBase = ListViewDataBase> {
  constructor(
    private readonly service: AbstractListViewService<D>,
    private readonly dialog: MatDialog,
    private readonly toast: ToastService,
    private readonly listViewQuickFilterKeyService: ListViewQuickFilterKeysService,
  ) {}

  @Input() name = '';
  @Input() canEdit = true;
  @Input() defaultQuickFilters: QuickFilterModel[] = [];

  @Input() showDefault = true;
  @Input() showResetView = true;
  @Input() showLoader = false;

  @Input() gridApi!: GridApi;
  @Input() columnApi!: ColumnApi;

  @Input() set quickFilterKey(quickFilterKey: string) {
    const userId = this.listViewQuickFilterKeyService.getPrivateQuickFilterKey();
    const spaceId = this.listViewQuickFilterKeyService.getPublicQuickFilterKey()!;

    this.personalQuickFilterKey$$.next(`${userId}:filter:${quickFilterKey}`);
    this.orgSpaceQuickFilterKey$$.next(`${spaceId}:filter:${quickFilterKey}`);
  }

  @Output() resetView = new EventEmitter<void>();
  @Output() filterSet = new EventEmitter<void>();

  filtersLoading = true;

  readonly trackByID = getTrackByFn<QuickFilterModel>('id');

  private readonly personalQuickFilterKey$$ = new BehaviorSubject('');
  private readonly orgSpaceQuickFilterKey$$ = new BehaviorSubject('');

  readonly state = connectState(this, {
    personalFilterKey: this.personalQuickFilterKey$$,
    orgSpaceFilterKey: this.orgSpaceQuickFilterKey$$,
    personalFilters: this.personalQuickFilterKey$$.pipe(
      filter(Boolean),
      switchMap((filterKey) => this.service.getQuickFilters(filterKey)),
      tap(() => (this.filtersLoading = false)),
    ),
    orgSpaceFilters: this.orgSpaceQuickFilterKey$$.pipe(
      filter(Boolean),
      switchMap((filterKey) => this.service.getQuickFilters(filterKey)),
      tap(() => (this.filtersLoading = false)),
    ),
  });

  setFilter(quickFilter: QuickFilterModel) {
    this.gridApi.setFilterModel(quickFilter.filterModel);
    this.columnApi.applyColumnState({ state: quickFilter.columnStates || [] });
    this.filterSet.emit();
  }

  addQuickFilter() {
    const quickFilter = new QuickFilterModel({
      name: '',
      editable: true,
      default: false,
      filterModel: this.gridApi.getFilterModel(),
      columnStates: this.columnApi.getColumnState(),
    });

    const data: ListViewAddEditQuickFilterDialogData = {
      isEdit: false,
      quickFilter,
    };

    this.dialog
      .open(ListViewAddEditQuickFilterDialogComponent, { width: '450px', data })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap((dialogData) => {
          const key = dialogData.isPrivate
            ? this.personalQuickFilterKey$$.value!
            : this.orgSpaceQuickFilterKey$$.value!;
          return this.service.addQuickFilter(dialogData.filter, key).pipe(
            tap(() => {
              if (dialogData.isPrivate) {
                this.refreshPersonalFilters();
              } else {
                this.refreshorgSpaceFilters();
              }
            }),
          );
        }),
        tap(() => this.openSnackBar('View created successfully')),
      )
      .subscribe();
  }

  editQuickFilter(quickFilter: QuickFilterModel, personal: boolean) {
    const data: ListViewAddEditQuickFilterDialogData = {
      isEdit: false,
      quickFilter: cloneDeep(quickFilter),
    };
    this.dialog
      .open(ListViewAddEditQuickFilterDialogComponent, { width: '450px', data })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap((dialogData) => {
          const key = personal ? this.personalQuickFilterKey$$.value! : this.orgSpaceQuickFilterKey$$.value!;
          return this.service.updateQuickFilter(dialogData.filter.id, dialogData.filter, key).pipe(
            tap(() => {
              if (personal) {
                this.refreshPersonalFilters();
              } else {
                this.refreshorgSpaceFilters();
              }
            }),
          );
        }),
        tap(() => this.openSnackBar('View updated successfully')),
      )
      .subscribe();
  }

  deleteQuickFilter(quickFilter: QuickFilterModel, personal: boolean) {
    this.dialog
      .open(ConfirmDialogComponent, {
        height: '250px',
        width: '600px',
        data: {
          data: quickFilter,
          text: `Are you sure you would like to delete <b>${quickFilter.name}?</b>`,
          confirmation_text: 'Delete',
          header_text: `Delete view?`,
        },
        panelClass: ['confirmation-pop-up'],
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap((data) => {
          const key = personal ? this.personalQuickFilterKey$$.value! : this.orgSpaceQuickFilterKey$$.value!;
          return this.service.deleteQuickFilter(data.id, key).pipe(
            tap(() => {
              if (personal) {
                this.refreshPersonalFilters();
              } else {
                this.refreshorgSpaceFilters();
              }
            }),
          );
        }),
        tap(() => this.openSnackBar('View deleted successfully')),
      )
      .subscribe();
  }

  private refreshPersonalFilters() {
    // retrigger subject
    this.personalQuickFilterKey$$.next(this.personalQuickFilterKey$$.value);
  }

  private refreshorgSpaceFilters() {
    // retrigger subject
    this.orgSpaceQuickFilterKey$$.next(this.orgSpaceQuickFilterKey$$.value);
  }

  private openSnackBar(message: string): void {
    this.toast.success(message);
  }
}
